import { useState, Fragment, useEffect, useMemo } from 'react'
import { Route, Redirect } from 'react-router-dom'
import { Header, Segment, Grid, Modal, Divider, List } from 'semantic-ui-react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PassengerHeader from '../Header/PassengerHeader'
import PassengerSidebarMenu from '../Sidebar/PassengerSidebarMenu'
import passengerActions from '../../store/actions/passengers'
import Swal from 'sweetalert2'
import { initSocketHandlers } from 'utils/socket/chat/handlers'
import { useLateralMenu } from 'hooks/useLateralMenu'
import { useTabNavigation, ViewRegistrar } from 'hooks/useTabNavigation'

const PrivatePassengerRouteContent = ({
	Component,
	routeProps,
	sidebarOptions,
	isAuthenticated,
	isAllowed,
	permissionsReady,
	passenger,
	logout,
	rest,
}) => {
	const { t } = useTranslation()
	const { registerActiveView, openTab } = useTabNavigation()
	const {
		setExpand,
		previousExpand,
		setPreviousExpand,
		mobileOnly,
		setMobileOnly,
	} = useLateralMenu()
	const [openModal, setOpenModal] = useState(false)
	const [sidebarMenuOptions, setSidebarMenuOptions] = useState(null)
	const handleSidebarMenuOpen = (options) => setSidebarMenuOptions(options)

	const memoizedRouteProps = useMemo(
		() => ({ ...routeProps, sidebarOptions: sidebarMenuOptions || sidebarOptions }),
		[routeProps, sidebarOptions, sidebarMenuOptions]
	)

	const memoizedLayoutConfig = useMemo(
		() => ({
			Header: PassengerHeader,
			Sidebar: PassengerSidebarMenu,
			sidebarWidth: 225,
			sidebarProps: {
				passenger,
				rest: rest,
				setExpand,
				previousExpand,
				setPreviousExpand,
				openModal,
				setOpenModal,
				mobileOnly,
				setMobileOnly,
				onMenuOpen: handleSidebarMenuOpen,
				onTabOpen: (route) => openTab(route, routeProps.history.push, false),
			},
			layoutProps: { logout: logout, rest: rest },
		}),
		[
			passenger,
			logout,
			rest,
			setExpand,
			previousExpand,
			setPreviousExpand,
			openModal,
			mobileOnly,
			setMobileOnly,
			handleSidebarMenuOpen,
			openTab,
			routeProps.history.push,
		]
	)

	if (!isAuthenticated) return <Redirect to='/login' />

	return (
		<>
			<ViewRegistrar
				registerActiveView={registerActiveView}
				Component={
					!permissionsReady ? EmptyComponent : isAllowed ? Component : ModuleNotFound
				}
				routeProps={memoizedRouteProps}
				layoutConfig={memoizedLayoutConfig}
			/>
			<Modal
				closeIcon
				basic
				centered={true}
				onClose={() => setOpenModal(false)}
				onOpen={() => setOpenModal(true)}
				open={openModal}
				size='large'
			>
				<Modal.Content>
					<Segment inverted style={{ height: '100%' }}>
						<Grid stackable divided inverted>
							<Grid.Row columns={2}>
								<Grid.Column width={6} verticalAlign='middle' textAlign='center'>
									<Header inverted content='© Geologistic GPS 2025' />
								</Grid.Column>
								<Grid.Column width={8}>
									<Header inverted content={t("Contacto")} />
									<Divider></Divider>
									<Header
										size='small'
										inverted
										content={t("¿Necesitas registrar un nuevo ticket o solicitud?")}
									/>
									<List divided inverted>
										<List.Item
											header={t("Ingresa al Portal: ")}
											content='https://geologistic.com.mx/Soporte/'
											style={{ cursor: 'pointer' }}
											onClick={() => {
												window.open(
													'https://geologistic.com.mx/Soporte/',
													'_blank'
												)
											}}
										/>
										<List.Item
											header={t("Soporte")}
											content='(686) 214 5447, (686) 385 6938,  (686) 510 9915'
										/>
										<List.Item header={t("Oficina")} content='(686) 372 9877' />
										<List.Item
											header={t("Emergencias")}
											content='(686) 420 3333, (686) 214 5447, (686) 385 6938, (686) 510 9915,  (686) 123 6454'
										/>
										<List.Item header={t("Administración")} content='(686) 122 0316' />
										<List.Item
											header={t("Ventas")}
											content='(686) 510 9915, (686) 196 4736, (686) 420 8586,  (686) 191 1571'
										/>
									</List>
								</Grid.Column>
							</Grid.Row>
						</Grid>
					</Segment>
				</Modal.Content>
			</Modal>
		</>
	)
}

const PrivatePassengerRoute = (props) => {
	const { t } = useTranslation()
	const { isAuthenticated = true, component: Component, ...rest } = props
	const dispatch = useDispatch()
	const [allowedRoutes, setAllowedRoutes] = useState([])
	const [isLoading, setIsLoading] = useState(true)
	const permissionsReady = !isLoading

	useEffect(() => {
		const loadPermissions = async () => {
			try {
				await props.getPassengerModules()
				const permissions = await props.getWebPassengerPermissions(props.passenger.id)
				setAllowedRoutes(permissions || [])
			} catch (error) {
				Swal.fire({
					icon: 'error',
					title: t('Error'),
					text: t('No se pudieron cargar los módulos del pasajero.'),
					showCloseButton: true,
				})
				setAllowedRoutes([])
			} finally {
				setIsLoading(false)
			}
		}
		loadPermissions()
	}, [props.passenger?.id, props.getPassengerModules, props.getWebPassengerPermissions, t])

	useEffect(() => {
		if (!props.passenger) return
		dispatch(initSocketHandlers())
	}, [dispatch, props.passenger])

	return (
		<Route
			{...rest}
			render={(routeProps) => {
				const isAllowed = allowedRoutes.some((allowedRoute) =>
					routeProps.location.pathname.startsWith(allowedRoute)
				)
				return (
					<PrivatePassengerRouteContent
						Component={Component}
						routeProps={routeProps}
						isAuthenticated={isAuthenticated}
						isAllowed={isAllowed}
						permissionsReady={permissionsReady}
						passenger={props.passenger}
						logout={rest.logout}
						rest={rest}
					/>
				)
			}}
		/>
	)
}

const mapStateToProps = (state) => ({
	isAuthenticated: !!state.app.passenger,
	passenger: state.app.passenger,
})

const mapDispatchToProps = (dispatch) => ({
	getPassengerModules: () => dispatch(passengerActions.getPassengerModules()),
	getWebPassengerPermissions: (passengerId) =>
		dispatch(passengerActions.getWebPassengerPermissions(passengerId)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PrivatePassengerRoute)

const EmptyComponent = () => null

const ModuleNotFound = () => {
	const { t } = useTranslation()
	return (
		<div style={{ padding: '50px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
			<Header as="h1" icon="warning circle" content={t("Módulo no encontrado")} />
			<p>{t("No tienes acceso a este módulo o no existe.")}</p>
		</div>
	)
}
